/**
 * Filter Panel Component - Product filtering UI
 */

import { FilterService } from '../services/FilterService.js';
import { escapeHtml } from '../../utils/validation.js';
import currencyManager from '../../utils/currency.js';
import { t } from '../utils/i18n.js';
import type { FilterParams } from '../../types/index.js';

export interface FilterPanelOptions {
    containerId?: string;
    onFilterChange?: (filters: FilterParams) => void;
    initialFilters?: FilterParams;
    suffix?: string;
}

export interface FilterFacetItem {
    id: number;
    name: string;
    product_count?: number;
}

export interface FilterFacetsData {
    stores?: FilterFacetItem[];
    selected_stores?: number[];
    price_range?: { min: number; max: number };
    unit_range?: { min: number; max: number };
}

/**
 * Filter Panel Component Class
 */
export class FilterPanel {
    private containerId: string;
    private onFilterChange: ((filters: FilterParams) => void) | null;
    private filterService: FilterService;
    private container: HTMLElement | null;
    private suffix: string;
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null;
    
    constructor(options: FilterPanelOptions = {}) {
        this.containerId = options.containerId || '';
        this.onFilterChange = options.onFilterChange || null;
        this.filterService = new FilterService(options.initialFilters || {});
        this.container = null;
        this.suffix = options.suffix || '';
        this.searchDebounceTimer = null;
    }

    /**
     * Initialize the filter panel
     */
    init(): void {
        this.container = document.getElementById(this.containerId);
        if (!this.container) {
            console.warn(`Filter panel container #${this.containerId} not found`);
            return;
        }

        // Hydrate existing elements instead of rendering from scratch
        this.attachEventListeners();
    }

    /**
     * Bind an external search input to this filter panel
     */
    bindSearchInput(inputId: string): void {
        const input = document.getElementById(inputId);
        if (input && input instanceof HTMLInputElement) {
            input.addEventListener('input', (e) => {
                if (this.searchDebounceTimer) {
                    clearTimeout(this.searchDebounceTimer);
                }
                this.searchDebounceTimer = setTimeout(() => {
                    this.filterService.setFilter('search', (e.target as HTMLInputElement).value);
                    this.triggerChange();
                }, 500);
            });
            
            // Set initial value if exists in filters
            const currentSearch = this.filterService.getFilters().search;
            if (currentSearch) {
                input.value = currentSearch;
            }
        }
    }

    /**
     * Update filter facets (stores, ranges) from API data
     */
    updateFacets(filtersData: FilterFacetsData | null | undefined): void {
        if (!filtersData || !this.container) {
            console.log('[FilterPanel] No filtersData or container', { filtersData, container: this.container });
            return;
        }

        console.log('[FilterPanel] updateFacets called with:', {
            stores: filtersData.stores?.length,
            suffix: this.suffix,
            containerId: this.containerId
        });

        // Hide skeleton loader
        const skeleton = document.getElementById(`filter-skeleton${this.suffix}`);
        if (skeleton) skeleton.style.display = 'none';

        // Update Stores
        this.renderCheckboxList('stores', filtersData.stores, filtersData.selected_stores);

        // Update Price Range placeholders/limits
        if (filtersData.price_range) {
            this.updateRangeInputs('price', filtersData.price_range);
        }

        // Update Unit Range - show all ranges from API (combines all unit types)
        if (filtersData.unit_range && filtersData.unit_range.max > 0) {
            this.updateRangeInputs('unit', filtersData.unit_range);
            // Show unit section
            const section = document.getElementById(`filter-unit-section${this.suffix}`);
            if (section) section.style.display = 'block';
        }
    }

    /**
     * Render a list of checkboxes.
     */
    renderCheckboxList(
        type: 'stores', 
        items: FilterFacetItem[] | null | undefined, 
        selectedIds: number[] = []
    ): void {
        const listContainer = document.getElementById(`filter-${type}-list${this.suffix}`);
        const section = document.getElementById(`filter-${type}-section${this.suffix}`);
        
        if (!listContainer) return;

        if (!items || items.length === 0) {
            if (section) section.style.display = 'none';
            return;
        }

        if (section) section.style.display = 'block';

        // Merge default selected IDs with any already-checked boxes (desktop + mobile)
        const selectedSet = new Set(selectedIds.map(String));
        listContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(cb => selectedSet.add(cb.value));
        document.querySelectorAll<HTMLInputElement>(
            `#filter-${type}-list${this.suffix} input[type="checkbox"]:checked, #filter-${type}-list${this.suffix.replace('-mobile','')} input[type="checkbox"]:checked`
        ).forEach(cb => selectedSet.add(cb.value));

        const compareByCountThenName = (a: FilterFacetItem, b: FilterFacetItem): number => {
            const countA = Number(a?.product_count) || 0;
            const countB = Number(b?.product_count) || 0;
            if (countA !== countB) return countB - countA; // high to low
            return (a?.name || '').localeCompare(b?.name || '');
        };

        const sortedItems = [...items].sort(compareByCountThenName);

        listContainer.innerHTML = sortedItems.map(item => `
            <label class="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                <input type="checkbox" 
                    name="store_id" 
                    value="${item.id}"
                    class="filter-checkbox rounded border-brand-primary text-brand-primary focus:ring-brand-primary"
                    style="color: #2e1818; border-color: #2e1818;"
                    ${selectedSet.has(String(item.id)) ? 'checked' : ''}
                >
                <span class="text-sm text-slate-700 flex-1 truncate">${escapeHtml(item.name)}</span>
                <span class="text-xs text-slate-400">(${item.product_count || 0})</span>
            </label>
        `).join('');

        // Re-attach listeners to new checkboxes
        const checkboxes = listContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
        checkboxes.forEach(cb => cb.addEventListener('change', this.handleFilterChange));
    }

    /**
     * Update range inputs (min/max)
     */
    updateRangeInputs(type: 'price' | 'unit', range: { min: number; max: number }): void {
        // Price filter uses different ID format: filter-min-price, filter-max-price
        // Unit filter uses: filter-unit-min, filter-unit-max
        const minId = type === 'price' ? `filter-min-price${this.suffix}` : `filter-${type}-min${this.suffix}`;
        const maxId = type === 'price' ? `filter-max-price${this.suffix}` : `filter-${type}-max${this.suffix}`;
        
        const minInput = document.getElementById(minId);
        const maxInput = document.getElementById(maxId);
        const section = document.getElementById(`filter-${type}-section${this.suffix}`);

        if (section) section.style.display = 'block';

        // For price filters, show currency-formatted placeholder
        const formatPlaceholder = (value: number | null | undefined): string => {
            if (type === 'price' && value != null) {
                return currencyManager.formatCurrencyDisplay(value);
            }
            return String(value || '');
        };

        if (minInput && minInput instanceof HTMLInputElement) {
            minInput.placeholder = formatPlaceholder(range.min);
            minInput.min = String(range.min);
        }
        if (maxInput && maxInput instanceof HTMLInputElement) {
            maxInput.placeholder = formatPlaceholder(range.max);
            maxInput.max = String(range.max);
        }
    }

    /**
     * Update sort order buttons visibility based on sort_by value
     */
    updateSortOrderButtons(sortBy: string): void {
        const nameContainer = document.getElementById(`sort-order-name${this.suffix}`);
        const priceContainer = document.getElementById(`sort-order-price${this.suffix}`);
        const currentOrder = this.filterService.getFilters().order;
        
        // Hide both first
        if (nameContainer) nameContainer.style.display = 'none';
        if (priceContainer) priceContainer.style.display = 'none';
        
        // Show appropriate container based on sort_by
        if (sortBy === 'name') {
            if (nameContainer) {
                nameContainer.style.display = 'flex';
                const orderToUse = currentOrder || 'asc';
                this.selectSortOrderButton(nameContainer, orderToUse);
                this.filterService.setFilter('order', orderToUse);
            }
        } else if (sortBy === 'price' || sortBy === 'unit_price') {
            if (priceContainer) {
                priceContainer.style.display = 'flex';
                const orderToUse = currentOrder || 'asc';
                this.selectSortOrderButton(priceContainer, orderToUse);
                this.filterService.setFilter('order', orderToUse);
            }
        } else {
            // For other sort types, clear order filter (use backend default)
            this.filterService.setFilter('order', '');
            // Also clear button highlight
            [nameContainer, priceContainer].forEach(container => {
                if (!container) return;
                container.querySelectorAll('.sort-order-option').forEach(btn => {
                    btn.classList.remove('active-sort');
                    btn.classList.remove('bg-brand-primary', 'text-white', 'border-brand-primary');
                    btn.classList.add('bg-white', 'text-slate-700', 'border-gray-300');
                    (btn as HTMLElement).style.backgroundColor = '';
                    (btn as HTMLElement).style.borderColor = '';
                });
            });
        }
    }

    /**
     * Select a sort order button (UI only)
     */
    selectSortOrderButton(container: HTMLElement, order: string): void {
        container.querySelectorAll('.sort-order-option').forEach(btn => {
            const isActive = (btn as HTMLElement).dataset.order === order;

            btn.classList.toggle('active-sort', isActive);

            if (isActive) {
                btn.classList.add('bg-brand-primary', 'text-white', 'border-brand-primary');
                btn.classList.remove('bg-white', 'text-slate-700', 'border-gray-300');
                (btn as HTMLElement).style.backgroundColor = '#2e1818';
                (btn as HTMLElement).style.borderColor = '#2e1818';
            } else {
                btn.classList.remove('bg-brand-primary', 'text-white', 'border-brand-primary');
                btn.classList.add('bg-white', 'text-slate-700', 'border-gray-300');
                (btn as HTMLElement).style.backgroundColor = '';
                (btn as HTMLElement).style.borderColor = '';
            }
        });
    }

    /**
     * Attach event listeners to existing elements
     */
    attachEventListeners(): void {
        if (!this.container) return;

        // Helper to attach listener by ID
        const attachById = (id: string, event: string, handler: (e: Event) => void): void => {
            const el = document.getElementById(id + this.suffix);
            if (el) el.addEventListener(event, handler);
        };

        // Sort By
        attachById('filter-sort-by', 'change', (e) => {
            const target = e.target as HTMLSelectElement;
            const sortBy = target.value;
            this.filterService.setFilter('sort_by', sortBy);
            
            // Show/hide appropriate sort order buttons
            this.updateSortOrderButtons(sortBy);
            
            this.triggerChange();
        });

        // On Sale
        attachById('filter-on-sale', 'change', (e) => {
            const target = e.target as HTMLInputElement;
            this.filterService.setFilter('on_sale_only', target.checked);
            this.triggerChange();
        });

        // Price Range (debounced)
        const handleRange = (name: string, value: string): void => {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.filterService.setFilter(name, value);
                this.triggerChange();
            }, 500);
        };

        attachById('filter-min-price', 'input', (e) => handleRange('min_price', (e.target as HTMLInputElement).value));
        attachById('filter-max-price', 'input', (e) => handleRange('max_price', (e.target as HTMLInputElement).value));
        attachById('filter-unit-min', 'input', (e) => handleRange('unit_min', (e.target as HTMLInputElement).value));
        attachById('filter-unit-max', 'input', (e) => handleRange('unit_max', (e.target as HTMLInputElement).value));

        // Sort Order Buttons
        const orderContainer = document.getElementById(`sort-order-container${this.suffix}`);
        if (orderContainer) {
            orderContainer.querySelectorAll('.sort-order-option').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const target = e.target as HTMLElement;
                    // Find the visible container (name or price)
                    const visibleContainer = target.closest('[id^="sort-order-"]') as HTMLElement;
                    if (visibleContainer) {
                        // Use the helper method to update UI
                        this.selectSortOrderButton(visibleContainer, target.dataset.order || '');
                    }
                    
                    this.filterService.setFilter('order', target.dataset.order || '');
                    this.triggerChange();
                });
            });
        }

        // Initialize sort order UI based on current select value (e.g., restored state)
        const sortByEl = document.getElementById(`filter-sort-by${this.suffix}`);
        if (sortByEl && sortByEl instanceof HTMLSelectElement) {
            this.filterService.setFilter('sort_by', sortByEl.value);
            this.updateSortOrderButtons(sortByEl.value);
        }

        // Clear buttons
        const clearBtn = document.getElementById(`clearFilters${this.suffix}`);
        if (clearBtn) {
            clearBtn.addEventListener('click', this.handleClearFilters);
        }

        // Currency selector
        attachById('currency-toggle', 'change', (e) => {
            const target = e.target as HTMLSelectElement;
            const currency = target.value;
            currencyManager.setCurrency(currency);
            currencyManager.updateCurrencyButtonsUI();
            this.triggerChange();
        });
        currencyManager.updateCurrencyButtonsUI();
    }

    /**
     * Handle generic filter change (checkboxes, etc)
     */
    handleFilterChange = (e: Event): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        
        if (type === 'checkbox') {
            // Handle store checkbox filters.
            if (name === 'store_id') {
                // Keep desktop/mobile checkboxes in sync
                document.querySelectorAll<HTMLInputElement>(`input[name="${name}"][value="${value}"]`).forEach(cb => {
                    if (cb !== target) {
                        cb.checked = checked;
                    }
                });

                // Read directly from DOM to get the TRUE state
                // We need to check both desktop and mobile lists
                const checkedBoxes = document.querySelectorAll<HTMLInputElement>(
                    '#filter-stores-list input[type="checkbox"]:checked, #filter-stores-list-mobile input[type="checkbox"]:checked'
                );
                const newValues = Array.from(checkedBoxes).map(cb => cb.value);
                
                this.filterService.setFilter(name, newValues);
                // Also store plural form to match API expectations
                this.filterService.setFilter('store_ids', newValues);
            } else {
                this.filterService.setFilter(name, checked);
            }
        } else {
            this.filterService.setFilter(name, value);
        }
        
        this.triggerChange();
    };

    handleClearFilters = (): void => {
        this.filterService.clearFilters();
        // Reset UI inputs
        if (this.container) {
            this.container.querySelectorAll<HTMLInputElement>('input').forEach(input => {
                if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
                else input.value = '';
            });
        }
        this.triggerChange();
    };

    triggerChange(): void {
        if (this.onFilterChange) {
            this.onFilterChange(this.filterService.getFilters());
        }
    }

    getFilters(): FilterParams {
        return this.filterService.getFilters();
    }
}
